/* Cart offers (snippets/lp-cart-offers.liquid): the buttons in the drawer.
   swap:   add a product, then lower or remove the lines listed in data-remove
           ("variant:1" one unit off that variant's line, "variant:all" removes it): a set
           replacing its parts, a tester replacing the tester already in the cart. The add
           goes first, so a failed add never removes anything
   add:    add a product, data-qty of it (outlet, bestsellers)
   data-props are the line properties: _upgrade (cart-set / cart-outlet) so orders can be
   traced, _mr: freegift for a tester, exactly as Monk's picker adds it.
   [data-lp-gift-toggle] opens the free-tester row in place.
   [data-lp-bubble] tips point at their row's + (aim, below).

   Speed: the last request of an action asks Shopify for the drawer's sections in the same
   response (as the theme's own cart.js does) and the drawer is rendered from it, so there
   is no separate sections round trip. A swap reads the quantities to lower from the
   drawer's own rows and keys the update by variant id, so there is no cart.js read either;
   when a row is missing or ambiguous it falls back to re-reading the cart and keying by
   line (keys change when the discount starts to apply, so they are read after the add).
   Rendering goes through the theme's renderDrawer, which swaps only the parts that changed.

   Safety: the theme's quantity steppers queue changes by line NUMBER, so an offer action
   waits for that queue first (a change landing after the add would hit the wrong line),
   and while an action runs every click inside the drawer's rows and gift row is swallowed
   (#CartDrawer.lp-busy), mouse and keyboard alike. */
(() => {
  if (window.lpCartOffers) return;
  window.lpCartOffers = true;

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
  const SECTIONS = 'cart-drawer,cart-icon-bubble';
  const withSections = (body) => ({ ...body, sections: SECTIONS, sections_url: window.location.pathname });
  const drawerEl = () => document.querySelector('cart-drawer');

  const render = (sections) => {
    const drawer = drawerEl();
    const current = document.querySelector('#CartDrawer');
    const next = sections['cart-drawer'] && parse(sections['cart-drawer']).querySelector('#CartDrawer');
    if (current && next) {
      // #CartDrawer-Notices exists only in the part-based drawer (the old one has -CartItems too)
      const partial = drawer && typeof drawer.renderDrawer === 'function'
        && current.querySelector('#CartDrawer-Notices') && next.querySelector('#CartDrawer-Notices');
      if (partial) drawer.renderDrawer(sections['cart-drawer']);
      else current.innerHTML = next.innerHTML;
    }
    const bubble = document.getElementById('cart-icon-bubble');
    const nextBubble = sections['cart-icon-bubble'] && parse(sections['cart-icon-bubble']).querySelector('.shopify-section');
    if (bubble && nextBubble) bubble.innerHTML = nextBubble.innerHTML;
    // The theme makes its controls tabbable when it opens the drawer or publishes a cart
    // update; a redraw from here must do the same, or Checkout drops out of the Tab order
    if (drawer && drawer.isOpen && typeof setTabindex === 'function' && drawer.toggleEelements) {
      setTabindex(drawer.toggleEelements(), '0');
    }
    // A button pressed with Enter is gone after the redraw: keep focus inside the dialog
    if (document.activeElement === document.body || document.activeElement === null) {
      const close = document.querySelector('#CartDrawer .wt-cart__drawer__close');
      if (close && drawer && drawer.isOpen) close.focus();
    }
  };

  const refresh = () =>
    fetch(root + '?sections=' + SECTIONS)
      .then((r) => r.json())
      .then(render)
      .catch(() => document.dispatchEvent(new CustomEvent('cart-drawer:refresh')));

  // Render from the mutation's own response when it carries the sections; otherwise fetch them
  const renderFrom = (json) => (json && json.sections && json.sections['cart-drawer'] ? render(json.sections) : refresh());

  // A network failure (connection dropped, not a cart error) is retried once
  const post = (path, body, retry = true) =>
    fetch(root + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then((r) => r.json().then((json) => {
      if (!r.ok || json.status) throw new Error(json.description || json.message || 'cart error');
      return json;
    }), (err) => (retry ? new Promise((res) => setTimeout(res, 400)).then(() => post(path, body, false)) : Promise.reject(err)));

  const props = (btn) => {
    try { return JSON.parse(btn.dataset.props || '{}'); } catch (err) { return {}; }
  };
  const addOne = (btn, sections) => {
    const body = { items: [{ id: Number(btn.dataset.variant), quantity: Number(btn.dataset.qty) || 1, properties: props(btn) }] };
    return post('cart/add.js', sections ? withSections(body) : body);
  };

  // The lines to lower, read from the drawer's rows: exactly one row per variant and its
  // quantity input, else null (then the cart is re-read). "all" needs no row: a tester's
  // line isn't rendered as a row while the gift row names it.
  const updatesFromRows = (specs) => {
    const updates = {};
    for (const { variant, amount } of specs) {
      if (amount === 'all') { updates[variant] = 0; continue; }
      const rows = document.querySelectorAll(`#CartDrawer .cart-item[data-variant-id="${variant}"]`);
      if (rows.length !== 1) return null;
      const input = rows[0].querySelector('.js-counter-quantity');
      if (!input) return null;
      // The live value: a tap on the theme's stepper changes it before its request is sent
      updates[variant] = Math.max(0, Number(input.value) - Number(amount));
    }
    return updates;
  };

  // Other scripts write the cart too (Monk stores an order id in the cart's attributes by
  // XHR after every change it notices), and Shopify's cart writes are last-writer-wins: a
  // write that read the cart a moment earlier can put the old lines back. So each action
  // checks the cart in its own response and corrects it once when that happened.
  // Monk's own cart requests go by XHR with ?mr=1; a write of ours waits until they have
  // been quiet for a moment (its order-id write follows its cart read within ~200 ms)
  let monkInFlight = 0, monkLast = 0;
  const xhrOpen = XMLHttpRequest.prototype.open, xhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) { this._lpMonk = /[?&]mr=1(&|$)/.test(String(url)); return xhrOpen.apply(this, arguments); };
  XMLHttpRequest.prototype.send = function () {
    if (this._lpMonk) { monkInFlight++; this.addEventListener('loadend', () => { monkInFlight--; monkLast = performance.now(); }); }
    return xhrSend.apply(this, arguments);
  };
  const monkQuiet = (limit = 1500) => new Promise((res) => {
    const t0 = performance.now();
    const tick = () => { if ((monkInFlight === 0 && performance.now() - monkLast > 250) || performance.now() - t0 > limit) res(); else setTimeout(tick, 50); };
    tick();
  });

  const qtyOf = (cart, variant) => cart.items.filter((i) => i.variant_id === variant).reduce((a, i) => a + i.quantity, 0);
  const keyUpdates = (cart, specs) => {
    const updates = {};
    specs.forEach(({ variant, amount }) => {
      cart.items.filter((item) => item.variant_id === variant).forEach((item, i) => {
        if (amount === 'all') updates[item.key] = 0;
        else if (i === 0) updates[item.key] = Math.max(0, item.quantity - Number(amount));
      });
    });
    return updates;
  };
  // What the cart should hold after a swap: the added variant, and each listed variant lowered
  const swapOk = (cart, added, want) => qtyOf(cart, added) >= 1 && Object.keys(want).every((v) => qtyOf(cart, Number(v)) === want[v]);

  const run = (btn) => {
    const action = btn.dataset.lpOfferAction;
    const added = Number(btn.dataset.variant);
    if (action === 'add') {
      return addOne(btn, true).then((json) => (json.items && !json.items.some((i) => i.variant_id === added) ? addOne(btn, true) : json));
    }
    if (action === 'swap') {
      const specs = (btn.dataset.remove || '').split(',').filter(Boolean).map((pair) => {
        const [variant, amount] = pair.split(':');
        return { variant: Number(variant), amount };
      });
      if (!specs.length) return addOne(btn, true);
      const fromRows = updatesFromRows(specs);
      // the quantities each listed variant should end at
      const want = {};
      if (fromRows) Object.keys(fromRows).forEach((v) => { want[v] = fromRows[v]; });
      const correct = (cart) => {
        // re-read the cart (fresh keys), re-add the set if it was lost, lower what is still too high
        return fetch(root + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json()).then((fresh) => {
          const again = qtyOf(fresh, added) < 1 ? addOne(btn, false).then(() => fetch(root + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json())) : Promise.resolve(fresh);
          return again.then((c) => {
            const updates = {};
            specs.forEach(({ variant, amount }) => {
              const target = amount === 'all' ? 0 : (variant in want ? want[variant] : Math.max(0, qtyOf(c, variant) - Number(amount)));
              if (qtyOf(c, variant) !== target) c.items.filter((i) => i.variant_id === variant).forEach((i, n) => { updates[i.key] = n === 0 ? target : 0; });
            });
            return Object.keys(updates).length ? post('cart/update.js', withSections({ updates })) : post('cart/update.js', withSections({ updates: {} }));
          });
        });
      };
      return monkQuiet().then(() => addOne(btn, false)).then(() => monkQuiet()).then(() => {
        btn.dataset.lpAdded = '1'; // the add went through: a failure from here on leaves the cart changed
        const first = fromRows
          ? post('cart/update.js', withSections({ updates: fromRows }))
          : fetch(root + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json()).then((cart) => {
              const updates = keyUpdates(cart, specs);
              specs.forEach(({ variant, amount }) => { want[variant] = amount === 'all' ? 0 : Math.max(0, qtyOf(cart, variant) - Number(amount)); });
              return post('cart/update.js', withSections({ updates }));
            });
        return first.then((json) => (json && json.items && !swapOk(json, added, want) ? correct(json) : json))
          // A late write by another script can still undo the switch after this response:
          // look once more a moment after the redraw, and put it right if so
          .then((json) => { btn._lpVerify = () => fetch(root + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json()).then((c) => (swapOk(c, added, want) ? null : correct(c).then(renderFrom))); return json; });
      });
    }
    return Promise.reject(new Error('unknown action'));
  };

  // The theme's stepper queue: a tap made just before an offer button must land first
  const themeIdle = () => {
    const items = document.querySelector('cart-drawer-items');
    if (!items || !items.requestQueue) return Promise.resolve();
    if (items.pendingInputs && items.pendingInputs.size && typeof items.flushChanges === 'function') items.flushChanges();
    return items.requestQueue.catch(() => {});
  };

  // Where an error message goes: the tip's text, the set card's price line, the outlet or
  // bestseller card's price, or the gift row's text
  const errorTarget = (btn) =>
    (btn.closest('.lp-tip') && btn.closest('.lp-tip').querySelector('.lp-tip__text')) ||
    (btn.closest('.lp-setcard') && btn.closest('.lp-setcard').querySelector('.lp-setcard__price')) ||
    (btn.closest('.lp-outlet__card') && btn.closest('.lp-outlet__card').querySelector('.lp-muted')) ||
    (btn.closest('.lp-gift') && btn.closest('.lp-gift').querySelector('.lp-gift__text')) ||
    (btn.closest('.lp-more__card') && btn.closest('.lp-more__card').querySelector('.lp-muted'));

  // The "press +" bubble: tint the + of the row above it and aim the bubble's point at it.
  // Reads both rects before the one write, and writes only when the value changed.
  const aim = () => {
    document.querySelectorAll('.wt-cart__drawer .js-counter-increase.lp-plus-hl').forEach((b) => b.classList.remove('lp-plus-hl'));
    document.querySelectorAll('[data-lp-bubble]').forEach((tip) => {
      const row = tip.previousElementSibling;
      const plus = row && row.querySelector('.js-counter-increase');
      if (!plus) return;
      plus.classList.add('lp-plus-hl');
      const p = plus.getBoundingClientRect(), t = tip.getBoundingClientRect();
      if (!t.width) return;
      const caret = `${Math.round(p.left + p.width / 2 - t.left)}px`;
      if (tip.style.getPropertyValue('--lp-caret') !== caret) tip.style.setProperty('--lp-caret', caret);
    });
  };
  let aimQueued = false;
  const queueAim = () => { if (aimQueued) return; aimQueued = true; requestAnimationFrame(() => { aimQueued = false; aim(); }); };
  new MutationObserver(queueAim).observe(drawerEl() || document.body, { childList: true, subtree: true });
  window.addEventListener('resize', queueAim);
  document.addEventListener('click', (e) => { if (e.target.closest('.wt-cart__trigger, [data-cart-open]')) setTimeout(aim, 350); });
  queueAim();

  // While an action runs, nothing in the rows or the gift row may act: not the theme's
  // steppers and bins, not a second offer button. Mouse clicks are already blocked by CSS;
  // this catches Enter/Space on a focused control too.
  document.addEventListener('click', (e) => {
    if (!document.querySelector('#CartDrawer.lp-busy')) return;
    if (e.target.closest('#CartDrawer-CartItems, .lp-gift, [data-lp-offer-action]') && !e.target.closest('[aria-busy="true"]')) {
      e.stopPropagation();
      e.preventDefault();
    }
  }, true);

  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-lp-gift-toggle]');
    if (toggle) {
      e.preventDefault();
      const opts = toggle.closest('.lp-gift').querySelector('.lp-gift__opts');
      const open = toggle.getAttribute('aria-expanded') !== 'true';
      toggle.setAttribute('aria-expanded', String(open));
      if (opts) opts.hidden = !open;
      // Open, the button closes the list: "Close"; closed again, back to "Choose" / "Change"
      if (!toggle.dataset.labelOpen) toggle.dataset.labelOpen = toggle.textContent.trim();
      toggle.textContent = open ? toggle.dataset.labelClose || toggle.dataset.labelOpen : toggle.dataset.labelOpen;
      return;
    }

    const btn = e.target.closest('[data-lp-offer-action]');
    if (!btn || btn.getAttribute('aria-busy') === 'true') return;
    e.preventDefault();
    if (btn.getAttribute('aria-pressed') === 'true') return; // the tester already chosen
    if (document.querySelector('#CartDrawer.lp-busy')) return;
    btn.setAttribute('aria-busy', 'true');
    if (btn.classList.contains('lp-gift__opt')) btn.setAttribute('aria-pressed', 'true');
    const drawer = document.querySelector('#CartDrawer');
    if (drawer) drawer.classList.add('lp-busy');
    themeIdle()
      .then(() => run(btn))
      .then(renderFrom)
      .then(() => { if (btn._lpVerify) setTimeout(() => btn._lpVerify().catch(() => {}), 1500); })
      .finally(() => {
        const current = document.querySelector('#CartDrawer');
        if (current) current.classList.remove('lp-busy');
      })
      .catch(() => {
        btn.removeAttribute('aria-busy');
        if (btn.classList.contains('lp-gift__opt')) btn.setAttribute('aria-pressed', 'false');
        const target = errorTarget(btn);
        if (target && btn.dataset.error) {
          target.textContent = btn.dataset.error;
          target.classList.add('lp-error-text');
          target.setAttribute('role', 'alert');
        }
        // A swap can fail after its add went through: show the real cart once the message was read
        if (btn.dataset.lpAdded) setTimeout(refresh, 3000);
      });
  });
})();
